import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  Alert,
  Chip,
  Button
} from '@mui/material';
import {
  Security as PrivacyIcon,
  Info as InfoIcon,
  Cookie as CookieIcon,
  Shield as ProtectionIcon,
  AccountBox as RightsIcon,
  Storage as DataIcon,
  Share as SharingIcon,
  Public as TransferIcon,
  Update as UpdateIcon,
  Email as ContactIcon,
  ArrowBack
} from '@mui/icons-material';

const PrivacyPolicyScreen = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const informationTypes = [
    'Identification Information – Name, email address, username, phone number, and optional profile details.',
    'Electronic Data – IP address, browser type/version, operating system, login timestamps, and approximate geolocation.',
    'Payment Information – For VIP membership, payments are processed securely through Stripe. We do not store payment card details.',
    'Preferences & Interests – Newsletter subscriptions, notification settings, and personalization choices.'
  ];

  const dataUses = [
    'Create and manage your account.',
    'Provide, maintain, and improve our services.',
    'Communicate with you about updates or changes.',
    'Personalize your experience and content.',
    'Send marketing communications if you opt in.',
    'Ensure security, detect fraud, and comply with legal obligations.'
  ];

  const cookieUses = [
    'Improve navigation and functionality.',
    'Analyze website traffic and behavior.',
    'Deliver personalized content and promotions.'
  ];

  const userRights = [
    'Access the personal information we hold about you.',
    'Request correction or deletion of your data.',
    'Restrict or object to certain processing.',
    'Withdraw consent for marketing or data processing at any time.',
    'Request your data in a portable, machine-readable format.'
  ];

  const sharingPartners = [
    'Service Providers – For analytics, development, maintenance, and marketing support.',
    'Legal Authorities – When required by law or regulation.'
  ];

  return (
    <Box sx={{ py: 2 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
        >
          Back
        </Button>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <PrivacyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          How we collect, use, and protect your information
        </Typography>
        <Chip 
          label="Effective: June 3, 2025" 
          variant="outlined" 
          color="primary" 
          size="small"
        />
      </Box>

      {/* Introduction */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1" paragraph>
            This Privacy Policy explains how Bettor Advantage ("we", "us", "our") collects, uses, shares, and protects your personal information when you use our website and services at{' '}
            <Link 
              href="https://bettoradvantage.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              color="primary"
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              https://bettoradvantage.com/
            </Link>
            {' '}("Bettor Advantage"). We encourage you to read this policy carefully so you understand your rights and our responsibilities.
          </Typography>
        </CardContent>
      </Card>

      {/* Information We Collect */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Information We Collect
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            When you use Bettor Advantage, we may process the following types of information:
          </Typography>
          <List dense>
            {informationTypes.map((info, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={info}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {index < informationTypes.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* How We Use Your Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use your information to:
          </Typography>
          <List dense>
            {dataUses.map((use, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={use}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {index < dataUses.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Cookies */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CookieIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Cookies
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Bettor Advantage uses cookies and similar technologies to:
          </Typography>
          <List dense>
            {cookieUses.map((use, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={use}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {index < cookieUses.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            You can manage your cookie preferences in your browser. For details, see our Cookie Policy.
          </Typography>
        </CardContent>
      </Card>

      {/* Data Protection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ProtectionIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Data Protection
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            We take appropriate technical and organizational measures to protect your personal data against unauthorized access, misuse, loss, or disclosure. Access to personal data is limited to authorized personnel with a legitimate business need.
          </Typography>
        </CardContent>
      </Card>

      {/* Your Rights */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RightsIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Your Rights
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Depending on your location and applicable laws, you may have the right to:
          </Typography>
          <List dense>
            {userRights.map((right, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={right}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {index < userRights.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
          <Typography variant="body1" sx={{ mt: 2 }}>
            To exercise these rights, contact us at{' '}
            <Link 
              href="mailto:signup@bettoradvantage.com" 
              color="primary"
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              signup@bettoradvantage.com
            </Link>
            .
          </Typography>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DataIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Data Retention
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            We retain personal data for as long as your account remains active. If you delete your account, we erase your personal information unless retention is required by law.
          </Typography>
        </CardContent>
      </Card>

      {/* Sharing Your Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SharingIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Sharing Your Information
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            We may share data with:
          </Typography>
          <List dense>
            {sharingPartners.map((partner, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={partner}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {index < sharingPartners.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> We never sell your personal data to third parties.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* International Transfers */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TransferIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              International Transfers
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Your data may be transferred outside your country for service delivery or development. We apply safeguards consistent with data protection laws to ensure it remains protected.
          </Typography>
        </CardContent>
      </Card>

      {/* Changes to This Policy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <UpdateIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Changes to This Policy
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            This Privacy Policy is effective June 3, 2025. We may update it periodically.
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Essential changes (e.g., new services or data uses) will be communicated before they take effect."
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <Divider component="li" />
            <ListItem>
              <ListItemText
                primary="Minor changes (e.g., clarifications, formatting) may be made without notice."
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Contact Us */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ContactIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Contact Us
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or how your data is handled, please contact us at:
          </Typography>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Link 
              href="mailto:signup@bettoradvantage.com" 
              color="primary"
              sx={{ 
                textDecoration: 'none', 
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}
            >
              signup@bettoradvantage.com
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PrivacyPolicyScreen;
